import React, { useEffect, useMemo, useState } from 'react';
import { logError } from 'utils/logger';
import { CircularProgress, Stack } from '@mui/material';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useQuery } from '@apollo/client';
import { bpfAPI, BPFAPINameSpase } from 'modules/stages';
import { Nullable, QueryListResponse, SelectOption } from 'global';
import { MoveCardEventParams } from 'components/shared/Board/types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { dealPipeLineApi, initialVOFiltersValue, VOFilters } from 'modules/vendorOpportunity';

import { useVO } from '../../hooks/useVO';
import { DealPipeLine } from './DealPipeLine';
import { generateBoardColumns } from '../lib';
import { DealPipelineListItem, DealPipelineResponse, DealPipelineVariables } from '../types';

interface Props {
  accountId: string;
  filters: Nullable<Partial<VOFilters>>;
}

export const DealPipelineCard = ({ accountId, filters = initialVOFiltersValue }: Props) => {
  const { handleUpdateCurrentStage } = useVO();
  const { showNotification } = useNotifications();

  const [selectedDepartment, setDepartment] = useState<Nullable<string>>(null);

  const mappedFilters: DealPipelineVariables['input']['filters'] = useMemo(
    () => ({
      isIncludeClosedDeals: filters?.CLOSED_DEALS?.isIncludeClosedDeals ?? false,
      created: {
        from: filters?.CREATE_DATE_RANGE?.from?.toISOString() || new Date().toISOString(),
        to: filters?.CREATE_DATE_RANGE?.to?.toISOString() || new Date().toISOString(),
      },
    }),
    [filters]
  );

  const [onFetchDealPipelineData, { data: listResponse, loading: isDealInfoLoading }] = useLazyQuery<
    DealPipelineResponse,
    DealPipelineVariables
  >(dealPipeLineApi.fetchListWithSummary, { notifyOnNetworkStatusChange: true });

  const {
    data: departments,
    error: departmentsError,
    loading: isDepartmentsLoading,
  } = useQuery<BPFAPINameSpase<QueryListResponse<SelectOption>>>(bpfAPI.department.fetchDepartmentsOptions, {
    onCompleted({ bpf }) {
      if (!bpf?.result?.list?.length) {
        showNotification('Flows empty!', { type: 'info' });
        return;
      }

      const defaultDepartmentId = bpf.result?.list[0].value;

      setDepartment(defaultDepartmentId);

      onFetchDealPipelineData({
        variables: {
          input: {
            accountId,
            filters: mappedFilters,
            departmentId: defaultDepartmentId,
          },
        },
      }).catch(logError);
    },
  });

  useEffect(() => {
    if (selectedDepartment && !isDealInfoLoading) {
      onFetchDealPipelineData({
        variables: {
          input: { accountId, filters: mappedFilters, departmentId: selectedDepartment },
        },
      }).catch(logError);
    }
  }, [mappedFilters, selectedDepartment]);

  const groupedVOs = listResponse?.dealPipeline?.entity ?? [];
  const columns = generateBoardColumns(groupedVOs);

  const handleMoveCard = async ({ cardEntity }: MoveCardEventParams & { cardEntity: DealPipelineListItem }) => {
    await handleUpdateCurrentStage({
      voId: cardEntity.deal.id,
      flowId: cardEntity.deal.flowId,
      stageId: cardEntity.stage.businessProcessFlowStageId,
    });
  };

  return (
    <RenderWithFallback
      error={departmentsError}
      isLoading={isDepartmentsLoading}
      fallBack={
        <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Stack>
      }
    >
      <DealPipeLine
        columns={columns}
        isStagesLoading={false}
        onMoveCard={handleMoveCard}
        isDealInfoLoading={isDealInfoLoading}
        isDepartmentsLoading={isDepartmentsLoading}
        departments={departments?.bpf?.result?.list ?? []}
        summary={listResponse?.dealPipeline?.summary ?? null}
        onDepartmentChange={async departmentId => {
          setDepartment(departmentId);
        }}
      />
    </RenderWithFallback>
  );
};
